import { Check, Copy, Lock, TriangleAlert } from 'lucide-react';
import { useCallback, useState } from 'react';

import MinistryIcon from '@/components/MinistryIcon';
import { cn } from '@/design-system/lib/cn';
import { IconTile } from '@/design-system/patterns/IconTile';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { DateField, TimeField } from '@/design-system/ui/DateField';
import { Input } from '@/design-system/ui/Input';
import { SegmentedControl } from '@/design-system/ui/SegmentedControl';
import { Stepper } from '@/design-system/ui/Stepper';
import { editGroupOf, editImpact } from '@escala/domain/eventEdit';
import { occurrenceBases } from '@escala/domain/occurrences';
import { seriesEndPlan, seriesEndSummary } from '@escala/domain/eventSeries';
import { seriesRule } from '@escala/domain/history';
import { recurrenceError, recurrenceLabel, ruleForDate } from '@escala/domain/recurrence';
import { formatShortDate } from '@escala/domain/dates';
import { paletteOf, seriesColor } from '@escala/domain/palette';
import { servedByRole } from '@escala/domain/roleRules';
import type { Ministry, ScheduleEvent } from '@escala/domain/types';
import type { EventDraft } from '@escala/domain/transitions';
import { type EditChoices, useStore } from '@/store/AppStore';

import ColorField from './ColorField';
import EditConfirmation from './EditConfirmation';
import NewOccurrence from './NewOccurrence';
import RecurrenceField from './RecurrenceField';

export function emptyDraft(date: string): EventDraft {
  return {
    mode: 'create',
    eventId: null,
    seriesId: null,
    isRecurring: false,
    scope: 'one',
    recurrence: null,
    title: '',
    color: null,
    date,
    time: '19:00',
    ministryIds: [],
    need: {},
  };
}

export function draftFromEvent(event: ScheduleEvent, scope: 'one' | 'series'): EventDraft {
  return {
    mode: 'edit',
    eventId: event.id,
    seriesId: event.seriesId,
    isRecurring: !!event.recurrence,
    scope: event.recurrence ? scope : 'one',
    recurrence: null,
    title: event.title,
    color: event.color,
    date: event.date,
    time: event.time,
    ministryIds: [...event.ministryIds],
    need: { ...event.need },
  };
}

/**
 * Criar ou editar evento. Na edição de uma ocorrência de série, escolhe-se se a mudança
 * vale para a data ou para a série. Avisa antes de salvar quando remover um ministério
 * (ou zerar uma função) apaga pessoas escaladas. Na criação, havendo evento único, dá
 * para escolher entre "Evento novo" e "Nova data de um evento" (`NewOccurrence`); o
 * "Nova data" do card de Eventos abre já nela, com o evento (`occurrenceOf`). O "Duplicar"
 * abre o evento novo já preenchido (`duplicateDraft`), avisando de onde veio (`copiedFrom`).
 */
export default function EventFormDialog({
  initial,
  occurrenceOf = null,
  copiedFrom = null,
  onClose,
}: {
  initial: EventDraft;
  occurrenceOf?: string | null;
  copiedFrom?: ScheduleEvent | null;
  onClose: () => void;
}) {
  const { state, today, actions } = useStore();
  const [kind, setKind] = useState<'event' | 'occurrence'>(occurrenceOf ? 'occurrence' : 'event');
  const [draft, setDraft] = useState(initial);
  const set = (patch: Partial<EventDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const setColor = useCallback((color: string | null) => setDraft((d) => ({ ...d, color })), []);
  const [confirming, setConfirming] = useState(false);
  const [choices, setChoices] = useState<EditChoices>({ includeScheduled: true, includePastEmpty: true });

  const isEdit = draft.mode === 'edit';
  const original = isEdit ? state.events.find((e) => e.id === draft.eventId) : undefined;
  const ministries = state.ministries;
  const selected = ministries.filter((m) => draft.ministryIds.includes(m.id));
  const qtyOf = (roleId: string, base: number) => draft.need[roleId] ?? base;
  // Mais de uma data: "Esta e as próximas" ou "Toda a série".
  const isSeries = isEdit && draft.isRecurring && draft.scope !== 'one';
  const impact = isEdit ? editImpact(state.events, draft, ministries, { today, ...choices }) : null;
  const groups = impact?.groups;
  // Data que já aconteceu com quem serviu marcado: dá para acrescentar, não para tirar ninguém.
  const lockedPast = isEdit && !isSeries && !!original && editGroupOf(original, today) === 'pastServed';
  const served = lockedPast && original ? servedByRole(original) : {};
  const roleServed = (roleId: string) => (served[roleId] ?? []).length > 0;
  const ministryLocked = (m: Ministry) => lockedPast && m.roles.some((r) => roleServed(r.id));
  const where = (dates: number) =>
    isSeries ? `em ${dates} ${dates === 1 ? 'data' : 'datas'}` : 'nesta data';
  const removedWithPeople = (impact?.ministries ?? []).map((x) => ({
    ministry: ministries.find((m) => m.id === x.ministryId)!,
    ...x,
  }));
  const needsConfirmation =
    isSeries &&
    !!groups &&
    groups.futureScheduled.length + groups.pastEmpty.length + groups.pastServed.length > 0;

  const total = selected.reduce((a, m) => a + m.roles.reduce((b, r) => b + qtyOf(r.id, r.qty), 0), 0);
  const customized = Object.entries(draft.need).some(([roleId, q]) => {
    for (const m of selected) {
      const role = m.roles.find((r) => r.id === roleId);
      if (role && role.qty !== q) return true;
    }

    return false;
  });
  // O término da série (esta e as próximas, ou toda): a regra, o fim atual e o que muda.
  const rule = isSeries && draft.seriesId ? seriesRule(state.events, draft.seriesId) : null;
  const endPlan =
    rule && draft.seriesId ? seriesEndPlan(state.events, draft.seriesId, draft.until ?? '', today) : null;
  const currentEnd = endPlan?.current ?? '';
  // `''` enquanto se digita: conta como mudado (e o "Salvar" espera a data inteira).
  const endChanged = draft.until != null && draft.until !== currentEnd;
  const endText = endChanged && endPlan ? seriesEndSummary(endPlan) : '';
  const canSave =
    draft.title.trim().length > 0 &&
    !!draft.time &&
    !!draft.date &&
    (!draft.recurrence || !recurrenceError({ ...draft.recurrence, start: draft.date })) &&
    !(endChanged && endPlan?.error);
  const future = groups ? groups.futureEmpty.length + groups.futureScheduled.length : 0;
  const seriesText = groups
    ? [
        `${future} ${future === 1 ? 'data' : 'datas'} a partir de hoje`,
        groups.pastEmpty.length
          ? `${groups.pastEmpty.length} ${groups.pastEmpty.length === 1 ? 'passada' : 'passadas'} sem registro`
          : '',
        groups.pastServed.length
          ? `${groups.pastServed.length} já ${groups.pastServed.length === 1 ? 'realizada' : 'realizadas'} (o histórico é preservado)`
          : '',
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const createLabel = draft.recurrence ? 'Criar série' : 'Criar evento';
  const scopeHint = {
    one: 'Só esta data muda; ela passa a aparecer como "Alterada" na série.',
    following: `${seriesText}. Muda esta data e as seguintes; as anteriores ficam como estão. Antes de salvar, você confirma onde aplicar.`,
    series: `${seriesText}. A data de cada uma não muda; antes de salvar, você confirma onde aplicar.`,
  }[draft.scope];

  const toggleMinistry = (id: string) => {
    const m = ministries.find((x) => x.id === id);

    if (m && draft.ministryIds.includes(id) && ministryLocked(m)) return;

    set({
      ministryIds: draft.ministryIds.includes(id)
        ? draft.ministryIds.filter((x) => x !== id)
        : [...draft.ministryIds, id],
    });
  };

  const save = () => {
    if (!canSave) return;

    if (needsConfirmation && !confirming) {
      setConfirming(true);

      return;
    }

    if (isEdit) actions.updateEvents(draft, choices);
    else actions.createEvents(draft);

    onClose();
  };
  const appliedCount = impact ? impact.applied.length : 0;
  let dialogTitle = isEdit ? 'Editar evento' : 'Novo evento';

  if (confirming) dialogTitle = 'Aplicar as alterações na série';
  let saveLabel = isEdit ? 'Salvar alterações' : createLabel;

  if (confirming) {
    saveLabel = appliedCount
      ? `Aplicar em ${appliedCount} ${appliedCount === 1 ? 'data' : 'datas'}`
      : 'Aplicar';
  }

  const switcher =
    !isEdit && occurrenceBases(state.events).length ? (
      <SegmentedControl
        aria-label="O que criar"
        block
        value={kind}
        onValueChange={setKind}
        options={[
          { value: 'event', label: 'Evento novo' },
          { value: 'occurrence', label: 'Nova data de um evento' },
        ]}
      />
    ) : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[min(540px,calc(100vw-24px))]">
        <DialogHeader title={dialogTitle} />
        {kind === 'occurrence' ? (
          <NewOccurrence seriesId={occurrenceOf} date={initial.date} header={switcher} onClose={onClose} />
        ) : (
          <>
            {confirming && impact ? (
              <DialogBody>
                <EditConfirmation
                  title={draft.title.trim()}
                  impact={impact}
                  choices={choices}
                  ministries={ministries}
                  onChange={setChoices}
                />
              </DialogBody>
            ) : null}
            <DialogBody className={confirming ? 'hidden' : undefined}>
              {switcher}
              {copiedFrom && kind === 'event' ? (
                <p className="m-0 flex items-start gap-2 rounded-control border border-primary-line bg-primary-faint px-3 py-2.5 text-small text-ink-secondary">
                  <Copy aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-primary" />
                  <span>
                    Cópia de <strong>{copiedFrom.title}</strong>: título, cor, horário, ministérios e
                    quantidades. Escolha a data e ajuste o que precisar; a escala não é copiada, e o novo
                    evento ganha o seu próprio card.
                  </span>
                </p>
              ) : null}
              {lockedPast ? (
                <p className="m-0 flex items-start gap-2 rounded-control border border-line-input bg-surface-subtle px-3 py-2.5 text-small text-ink-secondary">
                  <Lock aria-hidden className="mt-0.5 size-4 shrink-0 stroke-[1.8]" />
                  Este evento já aconteceu e tem quem serviu marcado. Dá para mudar o título e acrescentar
                  ministérios, funções ou vagas, mas não tirar ninguém do histórico, nem mudar a data e o
                  horário.
                </p>
              ) : null}
              <label className="flex flex-col gap-1.5">
                <span className="text-small font-semibold">Título</span>
                <Input
                  value={draft.title}
                  onChange={(e) => set({ title: e.target.value })}
                  placeholder="Ex.: Culto de Ação de Graças"
                />
              </label>

              <ColorField
                value={draft.color ?? null}
                automatic={draft.seriesId ? seriesColor(draft.seriesId) : null}
                onChange={setColor}
              />

              <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
                {!isEdit || draft.scope === 'one' ? (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-small font-semibold">Data</span>
                    <DateField
                      value={draft.date}
                      disabled={lockedPast}
                      onChange={(date) =>
                        // A repetição acompanha a data (semanal de domingo vira semanal de terça).
                        set({
                          date,
                          recurrence:
                            draft.recurrence && date
                              ? ruleForDate(draft.recurrence, draft.date, date)
                              : draft.recurrence,
                        })
                      }
                    />
                  </label>
                ) : null}
                <label className="flex flex-col gap-1.5">
                  <span className="text-small font-semibold">Horário</span>
                  <TimeField value={draft.time} disabled={lockedPast} onChange={(time) => set({ time })} />
                </label>
              </div>

              {isEdit && draft.isRecurring ? (
                <div className="flex flex-col gap-1.5">
                  <span className="text-small font-semibold">Aplicar alterações a</span>
                  <SegmentedControl
                    aria-label="Escopo da alteração"
                    block
                    value={draft.scope}
                    onValueChange={(scope) => set({ scope })}
                    options={[
                      { value: 'one', label: 'Só esta data' },
                      { value: 'following', label: 'Esta e as próximas' },
                      { value: 'series', label: 'Toda a série' },
                    ]}
                  />
                  <span className="text-caption text-ink-subtle">{scopeHint}</span>
                </div>
              ) : null}

              {rule ? (
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <label htmlFor="event-until" className="text-small font-semibold">
                      Termina em
                    </label>
                    <span className="text-caption text-ink-subtle">{recurrenceLabel(rule)}</span>
                    <span className="flex-1" />
                    {endChanged ? (
                      <Button variant="link" size="xs" onClick={() => set({ until: null })}>
                        Manter o término atual
                      </Button>
                    ) : null}
                  </div>
                  <DateField
                    id="event-until"
                    min={today}
                    value={draft.until ?? currentEnd}
                    onChange={(until) => set({ until: until === currentEnd ? null : until })}
                  />
                  <span
                    role={endChanged ? 'status' : undefined}
                    className={cn(
                      'text-caption',
                      endChanged && (endPlan?.error || endPlan?.remove.length)
                        ? 'text-status-conflict'
                        : 'text-ink-subtle'
                    )}
                  >
                    {endText ||
                      `A série vai até ${formatShortDate(currentEnd)}. Mude para criar as datas de mais tempo (pela repetição) ou encerrar antes.`}
                  </span>
                </div>
              ) : null}

              {!isEdit ? (
                <RecurrenceField
                  date={draft.date}
                  value={draft.recurrence}
                  onChange={(recurrence) => set({ recurrence })}
                />
              ) : null}

              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-small font-semibold">Ministérios envolvidos</span>
                  <span className="text-caption text-ink-subtle">Opcional</span>
                </div>
                {ministries.length ? (
                  <div className="flex flex-wrap gap-2">
                    {ministries.map((m) => {
                      const on = draft.ministryIds.includes(m.id);
                      const locked = on && ministryLocked(m);

                      return (
                        <button
                          key={m.id}
                          type="button"
                          aria-pressed={on}
                          disabled={locked}
                          title={
                            locked
                              ? 'Já há quem serviu neste ministério; o registro do histórico é mantido.'
                              : undefined
                          }
                          onClick={() => toggleMinistry(m.id)}
                          className={cn(
                            'inline-flex h-8.5 items-center gap-1.5 rounded-pill border-[1.5px] px-3 text-small font-medium',
                            on
                              ? 'border-primary bg-primary-soft'
                              : 'border-line-input bg-white hover:bg-surface-subtle'
                          )}
                        >
                          <span
                            aria-hidden
                            className="size-1.75 rounded-pill"
                            style={{ backgroundColor: m.color }}
                          />
                          {m.name}
                          {on ? <Check aria-hidden className="size-3.5 stroke-2 text-primary" /> : null}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="m-0 text-small text-ink-subtle">Nenhum ministério cadastrado ainda.</p>
                )}
                {draft.ministryIds.length ? null : (
                  <span className="text-caption text-ink-subtle">
                    Sem ministério, o evento aparece só no calendário, sem escala. Marque os ministérios que
                    servem nele para definir as funções e escalar pessoas.
                  </span>
                )}
              </div>

              {removedWithPeople.length ? (
                <p
                  role="alert"
                  className="m-0 flex items-center gap-2 rounded-control border border-status-conflict-line bg-danger-soft px-3 py-2.5 text-small text-status-conflict"
                >
                  <TriangleAlert aria-hidden className="size-4 shrink-0 stroke-[1.8]" />
                  {removedWithPeople
                    .map(
                      (x) =>
                        `Desmarcar ${x.ministry.name} remove ${x.removed} ${x.removed === 1 ? 'pessoa escalada' : 'pessoas escaladas'} ${where(x.removedDates)}`
                    )
                    .join('. ')}
                  .
                </p>
              ) : null}

              {selected.length ? (
                <div className="flex flex-col gap-2.5">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-small font-semibold">Funções necessárias</span>
                    <span className="text-meta text-ink-subtle">
                      {total} {total === 1 ? 'posição' : 'posições'} por evento
                    </span>
                    <span className="flex-1" />
                    {customized ? (
                      <Button variant="link" size="xs" onClick={() => set({ need: {} })}>
                        Igualar ao padrão
                      </Button>
                    ) : null}
                  </div>
                  <span className="-mt-1 text-caption text-ink-subtle">
                    Defina quantas pessoas cada função precisa neste evento. A geração automática usa essas
                    quantidades.
                  </span>
                  {selected.map((m) => (
                    <div key={m.id} className="overflow-hidden rounded-block border border-line-input">
                      <div className="flex items-center gap-2.5 border-b border-line-divider bg-surface-subtle px-3 py-2.5">
                        <IconTile size="xs" palette={paletteOf(m.color)}>
                          <MinistryIcon icon={m.icon} />
                        </IconTile>
                        <span className="flex-1 text-control font-semibold">{m.name}</span>
                        <span className="text-caption text-ink-subtle">{m.roles.length} funções</span>
                      </div>
                      {!m.roles.length ? (
                        <p className="m-0 px-3 py-2.5 text-meta text-ink-subtle">
                          Este ministério ainda não tem funções.
                        </p>
                      ) : null}
                      {m.roles.map((r) => {
                        const q = qtyOf(r.id, r.qty);
                        const effect = impact?.roles.find((x) => x.roleId === r.id);
                        const locked = lockedPast && roleServed(r.id);
                        const custom = q !== r.qty;

                        return (
                          <div
                            key={r.id}
                            className="flex flex-wrap items-center gap-2.5 border-t border-surface-muted px-3 py-2"
                          >
                            <div className="flex min-w-30 flex-1 flex-col">
                              <span
                                className={cn(
                                  'text-control font-medium',
                                  q === 0 ? 'text-ink-placeholder' : 'text-ink'
                                )}
                              >
                                {r.name}
                              </span>
                              <span
                                className={cn(
                                  'text-micro',
                                  custom ? 'text-primary-hover' : 'text-ink-subtle'
                                )}
                              >
                                {q === 0 ? 'Fora deste evento' : `Padrão: ${r.qty}`}
                                {custom ? (
                                  <>
                                    {' · '}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const need = { ...draft.need };
                                        delete need[r.id];
                                        set({ need });
                                      }}
                                      className="font-semibold text-primary"
                                    >
                                      restaurar
                                    </button>
                                  </>
                                ) : null}
                              </span>
                              {effect?.removed ? (
                                <span className="text-micro text-status-conflict">
                                  Remove {effect.removed}{' '}
                                  {effect.removed === 1 ? 'pessoa escalada' : 'pessoas escaladas'}{' '}
                                  {where(effect.removedDates)}
                                </span>
                              ) : null}
                              {effect?.extras ? (
                                <span className="text-micro text-ink-muted">
                                  {isSeries
                                    ? `Em ${effect.extraDates} ${effect.extraDates === 1 ? 'data há' : 'datas há'} mais pessoas que ${q}: ficam como extras`
                                    : `Há mais pessoas que ${q}: ficam como extras`}
                                </span>
                              ) : null}
                              {locked ? (
                                <span className="text-micro text-ink-subtle">
                                  Já há quem serviu: o registro é mantido
                                </span>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              disabled={locked && q > 0}
                              onClick={() =>
                                set({ need: { ...draft.need, [r.id]: q === 0 ? Math.max(1, r.qty) : 0 } })
                              }
                              className="rounded-chip px-1.5 py-1 text-caption text-ink-subtle hover:bg-surface-muted disabled:opacity-40"
                            >
                              {q === 0 ? 'Incluir' : 'Remover'}
                            </button>
                            <Stepper
                              aria-label={`Quantidade de ${r.name}`}
                              value={q}
                              min={locked ? 1 : 0}
                              max={20}
                              onValueChange={(v) => set({ need: { ...draft.need, [r.id]: v } })}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={confirming ? () => setConfirming(false) : onClose}>
                {confirming ? 'Voltar' : 'Cancelar'}
              </Button>
              <Button
                onClick={save}
                disabled={!canSave || (confirming && !appliedCount && !impact?.titleOnly.length)}
              >
                {saveLabel}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
